/**
 * contextUI.js — Контекстный UI: показывает только релевантные инструменты по модальности
 * Mikhmel Cornerstone4D DICOM Viewer Pro v5
 *
 * Подписывается на overlays:updated (после загрузки серии),
 * определяет контекст (CT chest / CT head / MRI brain / X-ray / cardio / etc.),
 * скрывает нерелевантные preset-chips и правые вкладки,
 * показывает кнопку «Все инструменты» для восстановления.
 *
 * Подключение в uiManager.js:
 *   import { initContextUI } from './contextUI.js';
 *   // внутри initUI(), после initFeatureBindings(ctx):
 *   initContextUI(ctx);
 */

import { on as busOn } from '../core/eventBus.js';
import { log } from '../utils/logger.js';

// [FIX] AbortController для очистки всех addEventListener при destroy
let _ac = new AbortController();


const $ = (id) => document.getElementById(id);
const $$ = (sel) => document.querySelectorAll(sel);

let _ctx = null;
let _contextMode = 'all'; // 'all' | detected context key
let _lastModality = '';
let _lastBodyContext = '';

// ══════════════════════════════════════════════════════════════
// CONTEXT PROFILES — what to show for each clinical context
// ══════════════════════════════════════════════════════════════

/**
 * Each profile defines:
 *   presets:    array of ww/wc pairs to keep visible (others get .ctx-dimmed)
 *   tabs:       array of right-sidebar tab IDs to keep in primary row
 *   tools:      array of toolbar data-tool values to highlight
 *   renderPresets: array of data-render-preset values to keep visible
 *   clinicalBtns: array of button IDs to promote (show)
 */
const CONTEXT_PROFILES = {
  'ct-chest': {
    label: 'CT Грудная клетка',
    icon: '🫁',
    presets: [
      { ww: 1500, wc: -600 }, // Лёгкие
      { ww: 350, wc: 50 },    // Мягк. ткани
      { ww: 2500, wc: 480 },  // Кости
      { ww: 350, wc: 50 },    // Средостение
      { ww: 700, wc: 100 },   // PE
      { ww: 400, wc: 40 },    // Грудная кл.
    ],
    tabs: ['tab-presets', 'tab-render', 'tab-ai', 'tab-clinical'],
    tools: ['WindowLevel', 'StackScroll', 'Zoom', 'Pan', 'Length', 'EllipticalROI'],
    renderPresets: ['ct-bone-3d', 'ct-lung-3d', 'ct-air', 'ct-vascular'],
    clinicalBtns: ['btn-emphysema', 'btn-agatston', 'btn-ctr'],   // [12.0.52] пневмоторакс удалён
  },

  'ct-head': {
    label: 'CT Голова',
    icon: '🧠',
    presets: [
      { ww: 80, wc: 40 },     // Мозг
      { ww: 40, wc: 40 },     // Инсульт
      { ww: 200, wc: 80 },    // Субдурал
      { ww: 2500, wc: 480 },  // Кости
      { ww: 350, wc: 50 },    // Мягк. ткани
    ],
    tabs: ['tab-presets', 'tab-render', 'tab-ai', 'tab-clinical'],
    tools: ['WindowLevel', 'StackScroll', 'Zoom', 'Length', 'Probe'],
    renderPresets: ['ct-bone-3d', 'mri-brain-3d'],
    clinicalBtns: [],   // [12.0.56] карточка «Инсульт» удалена из вкладки ИИ
  },

  'ct-cardiac': {
    label: 'CT Сердце / Ангио',
    icon: '❤️',
    presets: [
      { ww: 500, wc: 100 },   // Кардио
      { ww: 600, wc: 170 },   // Ангио
      { ww: 400, wc: 40 },    // Живот
      { ww: 2500, wc: 480 },  // Кости
      { ww: 700, wc: 100 },   // PE
    ],
    tabs: ['tab-presets', 'tab-render', 'tab-echo'],   // [12.0.53] расчёты по сердцу — во вкладке «ЭхоКГ»
    tools: ['WindowLevel', 'StackScroll', 'Zoom', 'Pan', 'Length', 'Crosshairs'],
    renderPresets: ['cardiac-3d', 'angio-3d', 'ct-vascular', 'ct-bone-3d'],
    clinicalBtns: ['btn-agatston', 'btn-ctr', 'btn-tavi', 'btn-cpr-vessel'],   // [12.0.52] вместо удалённого «Диаметра аорты»
  },

  'ct-abdomen': {
    label: 'CT Живот/Таз',
    icon: '🫃',
    presets: [
      { ww: 400, wc: 40 },    // Живот
      { ww: 150, wc: 30 },    // Печень
      { ww: 350, wc: 50 },    // Мягк. ткани
      { ww: 2500, wc: 480 },  // Кости
      { ww: 600, wc: 170 },   // Ангио
    ],
    tabs: ['tab-presets', 'tab-render', 'tab-ai', 'tab-clinical'],
    tools: ['WindowLevel', 'StackScroll', 'Zoom', 'Pan', 'Length', 'EllipticalROI', 'RectangleROI'],
    renderPresets: ['ct-bone-3d', 'ct-skin', 'ct-muscle', 'ct-vascular', 'ct-colon'],
    clinicalBtns: ['btn-cpr-vessel'],   // [12.0.52] «Диаметр аорты» удалён
  },

  'ct-spine': {
    label: 'CT Позвоночник',
    icon: '🦴',
    presets: [
      { ww: 2500, wc: 480 },  // Кости
      { ww: 300, wc: 40 },    // Позвоноч.
      { ww: 350, wc: 50 },    // Мягк. ткани
      { ww: 400, wc: 40 },    // Живот
    ],
    tabs: ['tab-presets', 'tab-render', 'tab-clinical', 'tab-measure'],
    tools: ['WindowLevel', 'StackScroll', 'Zoom', 'Length', 'Angle', 'CobbAngle'],
    renderPresets: ['ct-bone-3d', 'ct-muscle'],
    clinicalBtns: ['btn-bmd'],   // [12.0.52] «Переломы» удалены
  },

  'mri-brain': {
    label: 'MRI Мозг',
    icon: '🧠',
    presets: [
      { ww: 500, wc: 250 },   // T1
      { ww: 600, wc: 300 },   // T2
      { ww: 1200, wc: 600 },  // FLAIR
      { ww: 1000, wc: 500 },  // DWI
    ],
    tabs: ['tab-presets', 'tab-render', 'tab-ai', 'tab-clinical'],
    tools: ['WindowLevel', 'StackScroll', 'Zoom', 'Pan', 'Length', 'EllipticalROI'],
    renderPresets: ['mri-brain-3d', 'mri-wm-gm'],
    clinicalBtns: [],   // [12.0.56] карточка «Инсульт» удалена из вкладки ИИ
  },

  'mri-cardiac': {
    label: 'MRI Сердце',
    icon: '❤️',
    presets: [
      { ww: 500, wc: 250 },   // T1
      { ww: 600, wc: 300 },   // T2
    ],
    tabs: ['tab-presets', 'tab-render', 'tab-fusion', 'tab-echo'],   // [12.0.53] расчёты по сердцу — во вкладке «ЭхоКГ»
    tools: ['WindowLevel', 'StackScroll', 'Zoom', 'Pan', 'Length', 'EllipticalROI', 'Crosshairs'],
    renderPresets: ['cardiac-3d', 'mri-brain-3d'],
    // [12.0.52] Было ['btn-ctr', 'btn-tavi']: КТИ считается по плотности КТ и на МРТ
    // откажет. Для МР сердца — ФВ по маске ЛЖ на кино короткой оси.
    clinicalBtns: ['btn-ef-lvmask', 'btn-tavi'],
  },

  'xray': {
    label: 'Рентген',
    icon: '📷',
    presets: [
      { ww: 2048, wc: 1024 }, // Грудная кл.
      { ww: 2500, wc: 1000 }, // Кости
      { ww: 400, wc: 200 },   // Мягк. ткани
    ],
    tabs: ['tab-presets', 'tab-measure', 'tab-clinical'],
    tools: ['WindowLevel', 'Zoom', 'Pan', 'Length', 'Angle', 'ArrowAnnotate'],
    renderPresets: [],
    clinicalBtns: [],   // [12.0.52] рентген: КТИ здесь считается по КТ, «Переломы» удалены
  },

  'mammo': {
    label: 'Маммография',
    icon: '🩺',
    presets: [
      { ww: 2048, wc: 1024 }, // Маммо
    ],
    tabs: ['tab-presets', 'tab-measure', 'tab-ai'],
    tools: ['WindowLevel', 'Zoom', 'Pan', 'Length', 'Magnify', 'EllipticalROI'],
    renderPresets: [],
    clinicalBtns: [],
  },

  'pet': {
    label: 'PET / NM',
    icon: '☢️',
    presets: [
      { ww: 30000, wc: 15000 }, // PET
      { ww: 256, wc: 128 },     // NM
    ],
    tabs: ['tab-presets', 'tab-fusion', 'tab-render', 'tab-ai'],
    tools: ['WindowLevel', 'StackScroll', 'Zoom', 'Pan', 'EllipticalROI', 'Probe'],
    renderPresets: ['ct-vascular', 'ct-bone-3d'],
    clinicalBtns: [],
  },

  'us': {
    label: 'УЗИ',
    icon: '📡',
    presets: [
      { ww: 255, wc: 128 },   // US
    ],
    tabs: ['tab-presets', 'tab-measure', 'tab-echo'],   // [12.0.46] вкладка ЭхоКГ
    tools: ['WindowLevel', 'Zoom', 'Pan', 'Length', 'EllipticalROI'],
    renderPresets: [],
    clinicalBtns: [],
  },
};

// ══════════════════════════════════════════════════════════════
// CONTEXT DETECTION
// ══════════════════════════════════════════════════════════════

/**
 * Determine clinical context from modality + description + body part.
 * @param {Object} series - Series metadata
 * @returns {string} - Key from CONTEXT_PROFILES or 'all'
 */
function detectContext(series) {
  const mod = (series.modality || '').toUpperCase().split('/')[0].trim();
  const desc = (series.seriesDesc || series.studyDesc || '').toLowerCase();
  const body = (series.bodyPart || '').toLowerCase();

  // X-ray family
  if (['CR', 'DX', 'XR'].includes(mod)) return 'xray';
  if (mod === 'MG') return 'mammo';
  if (mod === 'US') return 'us';
  if (['PT', 'NM'].includes(mod)) return 'pet';

  // MRI
  if (mod === 'MR') {
    if (_matchAny(desc + ' ' + body, ['brain', 'мозг', 'head', 'голов', 'neuro'])) return 'mri-brain';
    if (_matchAny(desc + ' ' + body, ['cardiac', 'сердц', 'heart', 'cardio', 'cine'])) return 'mri-cardiac';
    return 'mri-brain'; // default MRI → brain
  }

  // CT — most complex routing
  if (mod === 'CT') {
    // Кардио/ангио — приоритет для кардиолога
    if (_matchAny(desc + ' ' + body, ['cardiac', 'сердц', 'heart', 'cardio', 'coronar', 'коронар', 'angio', 'ангио', 'aorta', 'аорт', 'cta', 'calcium'])) return 'ct-cardiac';
    // Голова
    if (_matchAny(desc + ' ' + body, ['brain', 'мозг', 'head', 'голов', 'skull', 'череп', 'stroke', 'инсульт'])) return 'ct-head';
    // Лёгкие/грудная клетка
    if (_matchAny(desc + ' ' + body, ['lung', 'лёгк', 'chest', 'грудн', 'thorax', 'тора', 'pulmon'])) return 'ct-chest';
    // Позвоночник
    if (_matchAny(desc + ' ' + body, ['spine', 'позвон', 'vertebr', 'lumbar', 'cervical', 'thoracic', 'поясн', 'шейн'])) return 'ct-spine';
    // Живот/таз
    if (_matchAny(desc + ' ' + body, ['abdom', 'живот', 'pelvis', 'таз', 'liver', 'печен', 'kidney', 'почк'])) return 'ct-abdomen';
    // Default CT → abdomen (самый частый пресет)
    return 'ct-chest';
  }

  return 'all'; // Unknown modality — show everything
}

function _matchAny(text, keywords) {
  return keywords.some(k => text.includes(k));
}

// ══════════════════════════════════════════════════════════════
// UI APPLICATION
// ══════════════════════════════════════════════════════════════

/**
 * Apply context profile to UI — dim irrelevant, highlight relevant.
 * @param {string} contextKey - Key from CONTEXT_PROFILES or 'all'
 */
function applyContext(contextKey) {
  _contextMode = contextKey;

  // 'all' — restore everything
  if (contextKey === 'all') {
    _restoreAll();
    _updateContextBadge(null);
    return;
  }

  const profile = CONTEXT_PROFILES[contextKey];
  if (!profile) { _restoreAll(); return; }

  log.debug(`[ContextUI] Applying context: ${contextKey} (${profile.label})`);

  // [11.1.25] ПОДКРАШИВАНИЕ ПРЕСЕТОВ ПРОФИЛЕМ СНЯТО ЦЕЛИКОМ — по слову
  // владельца: «не нужно подсвечивать самые продвинутые… меня путает,
  // значит всех путает; подсвечивать только активный».
  //
  // Как было. Профиль контекста приглушал «нерелевантные» чипы (ctx-dimmed)
  // и поднимал наверх «релевантные» (ctx-promoted). На экране это читалось
  // как СЕМЬ ВЫБРАННЫХ ПРЕСЕТОВ: яркие сверху, тусклые снизу — при том что
  // выбран всегда ровно один. В 11.1.23 сняли подчёркивающую полосу и
  // ослабили приглушение до 0.35 — легче не стало, потому что беда не в
  // силе эффекта, а в самом наличии второго «выделения» рядом с настоящим.
  // Теперь у чипов ОДНО состояние на экране — .active, и оно означает ровно
  // то, что нажал врач. Профиль продолжает работать там, где не спорит с
  // выбором: вкладки, инструменты, порядок — см. разделы ниже.
  //
  // Осторожно при возврате: любая «полезная подсказка» цветом на этих же
  // кнопках снова станет вторым выделением. Подсказки — в title, не в цвете.
  $$('.preset-chip[data-ww], .preset-chip[data-render-preset], .sl-vrt-btn[data-render-preset]')
    .forEach((chip) => chip.classList.remove('ctx-dimmed', 'ctx-promoted'));

  // ── 3. Right sidebar tabs — move relevant to primary row ──
  const primaryTabs = profile.tabs;
  // [11.0.2] Переменные tabToggle и overflowRow удалены: объявлялись и не
  // использовались ни разу. Раскрытием второго ряда вкладок ведает htmlInit,
  // здесь же вкладки только помечаются классами ctx-promoted-tab /
  // ctx-secondary-tab — двумя строками ниже, без обращения к этим узлам.

  $$('.right-tabs [data-tab], .right-tabs-secondary [data-tab]').forEach(tab => {
    const tabId = tab.dataset.tab;
    const isPrimary = primaryTabs.includes(tabId);
    tab.classList.toggle('ctx-promoted-tab', isPrimary);
    tab.classList.toggle('ctx-secondary-tab', !isPrimary);
  });

  // ── 4. Toolbar tool buttons — subtle highlight on relevant ──
  $$('.tool-btn[data-tool]').forEach(btn => {
    btn.classList.toggle('ctx-relevant', profile.tools.includes(btn.dataset.tool));
  });

  // ── 5. Clinical buttons — promote relevant ──
  profile.clinicalBtns.forEach(id => {
    const el = $(id);
    if (el) el.classList.add('ctx-promoted');
  });

  // ── 6. Update context badge ──
  _updateContextBadge(profile);

  // ── 7. Emit event ──
  if (window.__eventBus?.emit) {
    window.__eventBus.emit('context:changed', { context: contextKey, profile: profile.label });
  }
}

/**
 * Remove all context-specific classes, restore full UI.
 */
function _restoreAll() {
  $$('.ctx-dimmed').forEach(el => el.classList.remove('ctx-dimmed'));
  $$('.ctx-promoted').forEach(el => el.classList.remove('ctx-promoted'));
  $$('.ctx-promoted-tab').forEach(el => el.classList.remove('ctx-promoted-tab'));
  $$('.ctx-secondary-tab').forEach(el => el.classList.remove('ctx-secondary-tab'));
  $$('.ctx-relevant').forEach(el => el.classList.remove('ctx-relevant'));
  log.debug('[ContextUI] Restored all — no context filtering');
}

/**
 * Show/update context badge near topbar or overlay.
 * @param {Object|null} profile
 */
function _updateContextBadge(profile) {
  let badge = $('context-badge');

  if (!profile) {
    if (badge) badge.style.display = 'none';
    return;
  }

  if (!badge) {
    badge = document.createElement('div');
    badge.id = 'context-badge';
    badge.className = 'context-badge';
    badge.title = 'Контекстный режим — клик для отключения';
    badge.addEventListener('click', () => {
      applyContext('all');
      if (window.showToast) window.showToast('Контекстный режим отключён — все инструменты видны', 'info', 2000);
    }, { signal: _ac.signal });
    // Insert after active-tool-label in topbar
    const anchor = $('active-tool-label');
    if (anchor?.parentNode) {
      anchor.parentNode.insertBefore(badge, anchor.nextSibling);
    } else {
      document.body.appendChild(badge);
    }
  }

  badge.innerHTML = `<span class="ctx-badge-icon">${profile.icon}</span><span class="ctx-badge-label">${profile.label}</span><span class="ctx-badge-close" title="Показать все инструменты">✕</span>`;
  badge.style.display = 'inline-flex';

  badge.querySelector('.ctx-badge-close')?.addEventListener('click', (e) => {
    e.stopPropagation();
    applyContext('all');
    if (window.showToast) window.showToast('Все инструменты видны', 'info', 1500);
  }, { signal: _ac.signal });
}

// ══════════════════════════════════════════════════════════════
// PUBLIC API
// ══════════════════════════════════════════════════════════════

/**
 * Initialize context UI module.
 * @param {Object} ctx - Shared context from uiManager
 */
/**
 * [12.0.0] КОНТЕКСТНЫЕ ПОДСКАЗКИ ОТКЛЮЧЕНЫ.
 *
 * Слова владельца: «СТ грудная клетка — я думаю для врача это глупая подсказка,
 * все эти подсказки нужно отключить».
 *
 * Он прав, и вот чем это было плохо, а не только лишним. Программа определяла
 * область тела по срединной плотности среза и объявляла её врачу значком — то
 * есть сообщала специалисту то, что он видит на снимке лучше и раньше нас. А
 * заодно этот же механизм ПЕРЕКРАШИВАЛ панель: приглушал одни пресеты, поднимал
 * другие вкладки, помечал «нужные» кнопки. Врач при этом не просил ни того, ни
 * другого и не мог знать, почему панель у него не та, что вчера.
 *
 * ПОЧЕМУ ОТКЛЮЧЕНО ЦЕЛИКОМ, А НЕ ТОЛЬКО ЗНАЧОК. Значок был лишь видимой частью:
 * убрать его и оставить перекраску значило бы спрятать причину и сохранить
 * следствие. Всё, что делал этот механизм, теперь не делается.
 *
 * ЧТО ОСТАВЛЕНО. Сам разбор контекста (detectContext, профили) — на месте и
 * работает как прежде; он нужен другим местам и годится, если врач сам попросит
 * подобрать окно. Не работает только НЕПРОШЕНОЕ применение: подписка на загрузку
 * серии не заводится, значок не создаётся, классы не навешиваются.
 *
 * ВКЛЮЧИТЬ ОБРАТНО можно одной строкой в консоли — MikhmelViewer.контекстныеПодсказки(true).
 * Заведено не для врача, а чтобы не потерять работу насовсем.
 */
let _подсказкиВключены = false;

export function контекстныеПодсказки(включить) {
  _подсказкиВключены = !!включить;
  if (!_подсказкиВключены) {
    _restoreAll();
    const b = $('context-badge');
    if (b) b.style.display = 'none';
    _contextMode = 'all';
  }
  log.info(`[ContextUI] контекстные подсказки ${_подсказкиВключены ? 'включены' : 'отключены'}`);
  return _подсказкиВключены;
}

export function initContextUI(ctx) {
  _ctx = ctx;

  if (!_подсказкиВключены) {
    log.debug('[ContextUI] отключено по умолчанию (12.0.0) — панель не перекрашивается');
    return;
  }

  // Subscribe to series load event
  busOn('overlays:updated', (seriesUID) => {
    const allSeries = ctx.getAllSeries();
    const series = allSeries.find(s => s.seriesUID === seriesUID);
    if (!series) return;

    const context = detectContext(series);
    _lastModality = series.modality || '';
    _lastBodyContext = context;

    // Only apply if context actually changed
    if (context !== _contextMode) {
      applyContext(context);
    }
  });

  log.debug('[ContextUI] Initialized — listening for overlays:updated');
}

/**
 * Manually set context (for testing or manual override).
 * @param {string} contextKey - Key from CONTEXT_PROFILES or 'all'
 */
export function setContext(contextKey) {
  applyContext(contextKey);
}

/**
 * Get current context state.
 * @returns {Object}
 */
export function getContextState() {
  return {
    currentContext: _contextMode,
    lastModality: _lastModality,
    lastBodyContext: _lastBodyContext,
    availableContexts: Object.keys(CONTEXT_PROFILES).map(k => ({
      key: k, label: CONTEXT_PROFILES[k].label, icon: CONTEXT_PROFILES[k].icon,
    })),
  };
}

/**
 * Get available context profiles.
 * @returns {Object}
 */
export function getContextProfiles() {
  return { ...CONTEXT_PROFILES };
}

// [FIX] Cleanup all event listeners
export function destroyContextUI() {
  _ac.abort();
  _ac = new AbortController();
}
